import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, AlertCircle, Info, User, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDismissedUIElements } from '@/hooks/useDismissedUIElements';

interface ClientProfileCompletionBannerProps {
  needsOnboarding: boolean;
  profileCompletionPercentage: number;
  userProfileComplete: boolean;
  companyProfileComplete: boolean;
  missingItems: string[];
}

export const ClientProfileCompletionBanner: React.FC<ClientProfileCompletionBannerProps> = ({
  needsOnboarding,
  profileCompletionPercentage,
  userProfileComplete,
  companyProfileComplete,
  missingItems,
}) => {
  const navigate = useNavigate();
  const { isDismissed, dismiss } = useDismissedUIElements('client_profile_completion_success');

  // Show success banner if all complete and not dismissed
  if (!needsOnboarding && !isDismissed) {
    return (
      <Alert className="border-success bg-success/10">
        <CheckCircle2 className="h-5 w-5 text-success" />
        <div className="flex-1">
          <AlertTitle className="text-success">Your Profile is Complete!</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            You're all set to create jobs and manage your procurement activities.
          </AlertDescription>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => dismiss()}
          className="ml-auto"
        >
          Dismiss
        </Button>
      </Alert>
    );
  }

  // Don't show banner if complete and dismissed
  if (!needsOnboarding) {
    return null;
  }

  // Show warning if only one profile is incomplete
  if (userProfileComplete || companyProfileComplete) {
    const incompleteType = !userProfileComplete ? 'user' : 'company';
    
    return (
      <Alert className="border-warning bg-warning/10">
        <Info className="h-5 w-5 text-warning" />
        <div className="flex-1">
          <AlertTitle className="text-warning">
            {incompleteType === 'user' 
              ? 'Complete Your User Profile' 
              : 'Complete Your Company Profile'}
          </AlertTitle>
          <AlertDescription className="text-muted-foreground mt-2">
            {incompleteType === 'user'
              ? 'Add your personal details and procurement preferences to help vendors understand your needs.'
              : 'Add your company information to enhance vendor matching and credibility.'}
          </AlertDescription>
          <Button
            size="sm"
            variant="default"
            className="mt-3"
            onClick={() => navigate(incompleteType === 'user' ? '/profile/client' : '/profile/client?tab=company')}
          >
            {incompleteType === 'user' ? (
              <>
                <User className="h-4 w-4 mr-2" />
                Complete User Profile
              </>
            ) : (
              <>
                <Building2 className="h-4 w-4 mr-2" />
                Complete Company Profile
              </>
            )}
          </Button>
        </div>
      </Alert>
    );
  }

  // Show critical alert if both profiles incomplete
  return (
    <Alert className="border-destructive bg-destructive/10">
      <AlertCircle className="h-5 w-5 text-destructive" />
      <div className="flex-1">
        <AlertTitle className="text-destructive">Welcome! Complete Your Profile Setup</AlertTitle>
        <AlertDescription className="text-muted-foreground mt-2 space-y-3">
          <p>
            To get the most out of your procurement experience, please complete the following:
          </p>
          
          <div className="space-y-2">
            {missingItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div className="h-5 w-5 rounded-full border-2 border-muted-foreground flex items-center justify-center text-xs">
                  {index + 1}
                </div>
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Profile Completion:</span>
              <span className="text-muted-foreground">{profileCompletionPercentage}%</span>
            </div>
            <Progress value={profileCompletionPercentage} className="h-2" />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => navigate('/profile/client')}
            >
              <User className="h-4 w-4 mr-2" />
              Complete User Profile
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/profile/client?tab=company')}
            >
              <Building2 className="h-4 w-4 mr-2" />
              Complete Company Profile
            </Button>
          </div>
        </AlertDescription>
      </div>
    </Alert>
  );
};
